import _ from 'lodash';

import { dashboard } from 'types';
import { toJSON, toJSONArray } from '@tock/coretypes';
import AppConstants from 'constants/AppConstants';

interface GuestGroupsState {
  data: dashboard.GuestProfileGroup[];
  users: dashboard.GuestProfileGroupUser[];
  isInitialized: boolean;
  requestInProgress: boolean;
  selectedOptions: {
    page: number;
    filter: string;
    filterFocused: boolean;
  };
}

const initialSelectedOptions = {
  page: 0,
  filter: '',
  filterFocused: false,
};

export const initialState: GuestGroupsState = {
  data: [],
  users: [],
  isInitialized: false,
  requestInProgress: false,
  selectedOptions: _.cloneDeep(initialSelectedOptions),
};

export default function reducer(
  state: GuestGroupsState = initialState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  action: any
): GuestGroupsState {
  const { type, payload, context } = action;

  switch (type) {
    case AppConstants.GUEST_GROUP_ADD_DONE: {
      const createdGuestGroup = toJSON<dashboard.GuestProfileGroup>(payload);
      const data = _.concat(state.data, createdGuestGroup);

      return {
        ...state,
        data: _.sortBy(data, ['name']),
        requestInProgress: false,
      };
    }

    case AppConstants.GUEST_GROUP_BLUR_FILTER: {
      return {
        ...state,
        selectedOptions: {
          ...state.selectedOptions,
          filterFocused: false,
        },
      };
    }

    case AppConstants.GUEST_GROUP_CLEAR_FILTER: {
      return {
        ...state,
        selectedOptions: {
          ...state.selectedOptions,
          filter: '',
        },
      };
    }

    case AppConstants.GUEST_GROUP_DELETE_DONE: {
      const { id } = context;
      const data = _.reject(state.data, (group) => group.id === id);

      return {
        ...state,
        data: _.sortBy(data, ['name']),
        requestInProgress: false,
      };
    }

    case AppConstants.GUEST_GROUP_FETCH_USERS_STARTING: {
      return {
        ...state,
        users: [],
        isInitialized: true,
        requestInProgress: true,
      };
    }

    case AppConstants.GUEST_GROUP_FETCH_USERS_DONE: {
      const users = toJSONArray<dashboard.GuestProfileGroupUser>(payload);
      return {
        ...state,
        users,
        isInitialized: true,
        requestInProgress: false,
      };
    }

    case AppConstants.GUEST_GROUP_FETCH_ALL_DONE: {
      const data = _.sortBy(toJSONArray<dashboard.GuestProfileGroup>(payload), ['name']);
      return {
        ...state,
        data,
        isInitialized: true,
        requestInProgress: false,
      };
    }

    case AppConstants.GUEST_GROUP_FOCUS_FILTER: {
      return {
        ...state,
        selectedOptions: {
          ...state.selectedOptions,
          filterFocused: true,
        },
      };
    }

    case AppConstants.GUEST_GROUP_SET_FILTER: {
      return {
        ...state,
        selectedOptions: {
          ...state.selectedOptions,
          filter: payload,
        },
      };
    }

    case AppConstants.GUEST_GROUP_UPDATE_DONE: {
      const updatedGuestGroup = toJSON<dashboard.GuestProfileGroup>(payload);
      const data = _.concat(
        _.reject(state.data, (group) => group.id === updatedGuestGroup.id),
        updatedGuestGroup
      );

      return {
        ...state,
        data: _.sortBy(data, ['name']),
        requestInProgress: false,
      };
    }

    case AppConstants.GUEST_GROUP_ADD_STARTING:
    case AppConstants.GUEST_GROUP_ADD_EMAILS_STARTING:
    case AppConstants.GUEST_GROUP_DELETE_STARTING:
    case AppConstants.GUEST_GROUP_FETCH_ALL_STARTING:
    case AppConstants.GUEST_GROUP_REMOVE_EMAIL_STARTING:
    case AppConstants.GUEST_GROUP_UPDATE_STARTING: {
      return {
        ...state,
        requestInProgress: true,
      };
    }

    case AppConstants.GUEST_GROUP_ADD_EMAILS_FAILED:
    case AppConstants.GUEST_GROUP_ADD_FAILED:
    case AppConstants.GUEST_GROUP_DELETE_FAILED:
    case AppConstants.GUEST_GROUP_FETCH_ALL_FAILED:
    case AppConstants.GUEST_GROUP_FETCH_USERS_FAILED:
    case AppConstants.GUEST_GROUP_REMOVE_EMAIL_FAILED:
    case AppConstants.GUEST_GROUP_UPDATE_FAILED: {
      return {
        ...state,
        requestInProgress: false,
      };
    }

    case AppConstants.GUEST_GROUP_VIEW_PAGE: {
      return {
        ...state,
        selectedOptions: {
          ...state.selectedOptions,
          page: payload,
        },
      };
    }

    default: {
      return state;
    }
  }
}
